import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSolutionIdea, updateSolutionIdea } from '../api/solutions';
import { listCampaigns, syncCampaign } from '../api/meta';
import { useAppStore } from '../stores/app';

export function SolutionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessionId = useAppStore((s) => s.activeSessionId);
  const qc = useQueryClient();

  const { data: solution, isLoading } = useQuery({
    queryKey: ['solution', id],
    queryFn: () => getSolutionIdea(id!),
    enabled: !!id,
  });

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns', sessionId],
    queryFn: () => listCampaigns(sessionId!),
    enabled: !!sessionId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateSolutionIdea>[1]) =>
      updateSolutionIdea(id!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['solution', id] });
      qc.invalidateQueries({ queryKey: ['solutions'] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: (campaignId: string) => syncCampaign(campaignId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  if (isLoading || !solution) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const relatedCampaigns = (campaigns ?? []).filter(
    (c) => c.solution_idea_id === solution.id
  );

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl">
      {/* Back */}
      <button
        onClick={() => navigate('/solutions')}
        className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
      >
        ← Zurück zur Liste
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <h1 className="text-xl font-bold text-gray-900">{solution.name}</h1>
        <div className="flex gap-2">
          <button
            onClick={() => updateMutation.mutate({ focus: solution.focus ? 0 : 1 })}
            disabled={updateMutation.isPending}
            className={`px-3 py-1.5 text-sm rounded-lg ${
              solution.focus
                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {solution.focus ? '⭐ Fokus entfernen' : '☆ Als Fokus markieren'}
          </button>
        </div>
      </div>

      {/* Description */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Beschreibung</h2>
        <p className="text-gray-800 whitespace-pre-wrap">{solution.description || '–'}</p>
      </div>

      {/* Business Model */}
      {solution.business_model && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Business Model</h2>
          <p className="text-gray-800 whitespace-pre-wrap">{solution.business_model}</p>
        </div>
      )}

      {/* Transformation Statement */}
      {solution.transformation_statement && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Transformation Statement</h2>
          <p className="text-gray-800 italic">„{solution.transformation_statement}"</p>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-xs text-gray-500">CTR</p>
          <p className="text-xl font-bold text-gray-900">
            {solution.ctr != null ? `${(solution.ctr * 100).toFixed(2)}%` : '–'}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-xs text-gray-500">Budget</p>
          <p className="text-xl font-bold text-gray-900">
            {solution.budget != null ? `€${(solution.budget / 100).toFixed(2)}` : '–'}
          </p>
        </div>
      </div>

      {/* Related Campaigns */}
      {relatedCampaigns.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            Zugehörige Kampagnen ({relatedCampaigns.length})
          </h2>
          <div className="space-y-3">
            {relatedCampaigns.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between border border-gray-100 rounded-lg p-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">{c.headline}</p>
                  <p className="text-xs text-gray-500">
                    Status: {c.status} · Impressions: {c.impressions ?? 0} · Clicks: {c.clicks ?? 0} ·
                    Spend: €{((c.spend ?? 0) / 100).toFixed(2)}
                  </p>
                </div>
                <button
                  onClick={() => syncMutation.mutate(c.id)}
                  disabled={syncMutation.isPending}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
                >
                  🔄 Sync
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
